import { Link } from "react-router-dom";
import { CONTACT } from "@/constants/config";
import { Heart, PawPrint } from "lucide-react";
import PageMeta from "@/components/PageMeta";

const PILLARS = [
  { title: "Rescuing with Aloha", body: "The spirit of Aloha guides everything we do. It's about love, compassion, and respect for all living beings. We extend this spirit to every animal we rescue, offering them a chance at a new beginning filled with kindness and care." },
  { title: "Building forever ʻohana", body: "We are committed to finding forever homes where our rescued animals can thrive. Our goal is to create lasting connections, ensuring that every bulldog and companion animal becomes a cherished member of their new family, their forever ʻohana." },
  { title: "Mālama — to care", body: "Comprehensive veterinary care, behavioral support, and loving foster homes. We believe in building trust and fostering a sense of security for each rescued animal, preparing them for a joyful life." },
  { title: "Kuleana — responsibility", body: "We hold ourselves accountable to every dog we save, every donor who trusts us, and every family who opens their home. Kuleana is our promise: to see it through, with integrity." },
];

export default function About() {
  return (
    <div>
      <PageMeta
        title="About Us — Our Story & Mission | Lavish 808 Bulliez Rescued Paws"
        description="A 501(c)(3) Hawaiian-inspired bulldog rescue founded by Heather Souza. Based in Murrieta, California, saving bulldogs from Hawaiʻi to the West Coast since 2024."
      />
      <section className="video-hero h-[60vh] min-h-[420px] flex items-end">
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=85"
          alt="Hawaiʻi beach at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="about-hero-image"
        />
        <div className="overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16 text-white">
          <div className="text-xs uppercase tracking-[0.35em] mb-4 text-white/80">E komo mai • Welcome</div>
          <h1 className="font-serif text-5xl md:text-7xl mb-4">Aloha, we're Lavish 808 Bulliez.</h1>
          <p className="font-serif italic text-lg md:text-xl text-white/90 max-w-2xl">"{CONTACT.motto}"</p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl md:text-2xl leading-relaxed font-serif" style={{ color: "#0E5C6E" }}>
            From Hawaiʻi to the West Coast, our mission is simple:
            <em style={{ color: "#E27E4E" }}> no bulldog left behind.</em>
          </p>

          <div className="mt-10 space-y-6 text-lg leading-relaxed" style={{ color: "#2D2B2A" }}>
            <p>
              {CONTACT.org} is dedicated to rescuing, rehabilitating, and rehoming
              abandoned, neglected, surrendered, and at-risk Bulldogs, French Bulldogs, and bully
              breeds. Our mission is driven by compassion, veterinary care, and the spirit of Aloha —
              ensuring every animal finds their forever ʻohana.
            </p>
            <p>
              We transport dogs to and from Hawaiʻi, and have proudly placed pups with loving
              families across <strong>Hawaiʻi, Nevada, Arizona, and Washington</strong> — every
              paw a testament to what happens when compassion travels.
            </p>
            <p>
              We are a registered <strong>501(c)(3)</strong> nonprofit (EIN {CONTACT.ein}), <strong>established
              as an organization in 2026</strong> — though our founder has been rescuing bulldogs and saving lives since
              <strong> 2024</strong>. Based in Murrieta, California, we're run largely by volunteers. Every dollar donated
              goes directly to vet bills, medical care, transport, and feeding our pups while they wait for their
              forever ʻohana.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-16" style={{ backgroundColor: "#0E5C6E" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center text-white">
          <div className="md:col-span-4">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden mx-auto md:mx-0" style={{ backgroundColor: "#E27E4E" }}>
              <img
                src="https://customer-assets.emergentagent.com/job_bulldog-sanctuary/artifacts/jn04mkq3_705341454_26480441868323207_7516160053345616629_n.jpg"
                alt="Heather Souza — Founder"
                className="w-full h-full object-cover"
                data-testid="founder-photo"
              />
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] mb-3 text-white/70">Founder & President</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Heather Souza</h2>
            <p className="text-white/85 leading-relaxed">
              Heather brings decades of financial leadership — controller and CFO-level experience,
              small business ownership, and military leadership — to the rescue she now calls her
              life's work. A lifelong bulldog advocate, she has been rescuing dogs since <strong>2024</strong> and
              formally founded Lavish 808 Bulliez Rescued Paws as a 501(c)(3) nonprofit in <strong>2026</strong> to give
              at-risk bulldogs the second chance every paw deserves.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-3">
                <div className="text-xs uppercase tracking-[0.3em]" style={{ color: "#E27E4E" }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-3xl md:text-4xl mt-2" style={{ color: "#0E5C6E" }}>{p.title}</h3>
              </div>
              <div className="md:col-span-9 text-lg leading-relaxed" style={{ color: "#2D2B2A" }}>{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-serif text-3xl md:text-4xl mb-8" style={{ color: "#0E5C6E" }}>
            "Mahalo nui loa" — from every paw we've saved.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/donate" className="btn-donate">
              <Heart size={18} fill="white" /> Kōkua — Donate
            </Link>
            <Link to="/adopt" className="btn-outline"><PawPrint size={18} /> Meet our dogs</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
